import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { AutoAwesome } from "@mui/icons-material";
import { ValidationStatusChip, type ValidationStatus } from "./ValidationStatusChip";
import { AiSearchDialog } from "./AiSearchDialog";

interface Props {
  value: string;
  onChange: (v: string) => void;
  // True when the portal has an OpenAI key configured. The button
  // is hidden when false so users in deployments without the key
  // don't see a control that would 503.
  aiEnabled: boolean;
}

interface ValidateResponse {
  ok: boolean;
  message?: string;
}

// Step 3 — Lucene-syntax post-filter (optional).
//
// Applied per-page after the metadata filter passes; the worker fetches
// the XML, extracts transcript text, and matches it against the query.
// We validate (debounced) against /api/v1/search/validate so the user
// catches wildcards/fields/etc. before submit. Unlike the filter step,
// luqum doesn't expose source positions on parse failure, so the chip
// is the only diagnostic — no inline squigglies.

const VALIDATE_DEBOUNCE_MS = 350;

export function StepSearch({ value, onChange, aiEnabled }: Props) {
  const [status, setStatus] = useState<ValidationStatus>(
    value.trim() ? { kind: "checking" } : { kind: "empty" },
  );
  const [aiOpen, setAiOpen] = useState(false);

  useEffect(() => {
    if (!value.trim()) {
      setStatus({ kind: "empty" });
      return;
    }
    setStatus({ kind: "checking" });
    const controller = new AbortController();
    const handle = setTimeout(async () => {
      try {
        const r = await fetch("/api/v1/search/validate", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ query: value }),
          signal: controller.signal,
        });
        if (!r.ok) return;
        const data: ValidateResponse = await r.json();
        setStatus(
          data.ok
            ? { kind: "ok" }
            : { kind: "error", message: data.message ?? "invalid search" },
        );
      } catch {
        // Network blip / abort — leave status alone; next keystroke retries.
      }
    }, VALIDATE_DEBOUNCE_MS);
    return () => {
      controller.abort();
      clearTimeout(handle);
    };
  }, [value]);

  const isError = status.kind === "error";

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Optional. Apply a Lucene-syntax search to each page's transcript text. Pages that
        pass the metadata filter but don't match this search are dropped before they're
        written.
      </Typography>
      <TextField
        multiline
        fullWidth
        rows={3}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder='+"oil refinery"~10 -spilled'
        error={isError}
        InputProps={{
          sx: {
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
          },
        }}
      />
      <Stack
        direction="row"
        alignItems="center"
        gap={1.5}
        sx={{ mt: 1.5, flexWrap: "wrap" }}
      >
        <ValidationStatusChip
          status={status}
          emptyLabel="No search"
          okLabel="Search okay"
          errorPrefix="Search error"
        />
        <Box flexGrow={1} />
        {aiEnabled && (
          <Tooltip
            title={
              value.trim()
                ? "Refine the current query with AI"
                : "Build a query from a natural-language description"
            }
            placement="top"
          >
            <Button
              variant="outlined"
              size="small"
              startIcon={<AutoAwesome />}
              onClick={() => setAiOpen(true)}
            >
              Ask AI
            </Button>
          </Tooltip>
        )}
      </Stack>
      <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
        <Typography variant="caption" component="div">
          <strong>Search syntax</strong> (Lucene):
          <ul style={{ margin: "0.5em 0", paddingLeft: "1.5em" }}>
            <li>
              <code>term</code> — single word
            </li>
            <li>
              <code>"phrase"</code> — exact phrase
            </li>
            <li>
              <code>"phrase"~N</code> — phrase within N words
            </li>
            <li>
              <code>+required</code> — must match
            </li>
            <li>
              <code>-prohibited</code> — must not match
            </li>
            <li>
              <code>AND</code>, <code>OR</code>, <code>NOT</code>, parentheses
            </li>
          </ul>
          Wildcards, fuzzy, boost, and field-scoped queries aren't supported. Pages with no
          transcript content are skipped silently.
        </Typography>
      </Paper>
      <AiSearchDialog
        open={aiOpen}
        current={value}
        onClose={() => setAiOpen(false)}
        onApply={onChange}
      />
    </Box>
  );
}
